import { useState, useEffect } from 'react';
import { TrendingUp, Cpu, HardDrive, Activity, AlertTriangle, RefreshCw, Clock } from 'lucide-react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import StatCard from '../../components/common/StatCard';
import EdgeSelector from '../../components/common/EdgeSelector';
import { alertsService } from '../../services';
import { observabilityService } from '../../services/observability.service';
import { createChartTooltipFormatter, formatTimeOnly } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';

type TimeRange = '1h' | '6h' | '24h' | '7d' | '30d';

interface PerformanceAlert {
  id: string;
  severity: string;
  device: string;
  message: string;
  timestamp: string;
}

const Performance = () => {
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const [selectedEdgeId, setSelectedEdgeId] = useState<string>('');
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [loading, setLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [performanceAlerts, setPerformanceAlerts] = useState<PerformanceAlert[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(true);

  // Chart data
  const [bandwidthData, setBandwidthData] = useState<any[]>([]);
  const [cpuData, setCpuData] = useState<any[]>([]);
  const [memoryData, setMemoryData] = useState<any[]>([]);
  const [connectionsData, setConnectionsData] = useState<any[]>([]);
  const [packetLossData, setPacketLossData] = useState<any[]>([]);

  // Stats
  const [avgCpu, setAvgCpu] = useState(0);
  const [avgMemory, setAvgMemory] = useState(0);
  const [avgBandwidth, setAvgBandwidth] = useState(0);
  const [avgConnections, setAvgConnections] = useState(0);

  useEffect(() => {
    fetchAlerts(false);
    const interval = setInterval(() => fetchAlerts(true), 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async (isBackground = false) => {
    try {
      const options = isBackground ? { showLoading: false } : undefined;
      setAlertsLoading(true);
      const alerts = await alertsService.getAlerts(options);

      // Transform alerts to performance alert format
      const transformed: PerformanceAlert[] = alerts
        .filter(alert => alert.status.state === 'active') // Only show active alerts
        .map(alert => ({
          id: alert.fingerprint,
          severity: alert.labels.severity === 'critical' ? 'Critical' :
                   alert.labels.severity === 'warning' ? 'Warning' : 'Info',
          device: alert.labels.edge_name || alert.labels.edge_id || 'Unknown',
          message: alert.annotations.summary || alert.annotations.description || 'Alert triggered',
          timestamp: formatAlertTimestamp(alert.startsAt),
        }));

      setPerformanceAlerts(transformed);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setAlertsLoading(false);
    }
  };

  const formatAlertTimestamp = (timestamp: string): string => {
    const alertTime = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - alertTime.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  // Parse time range to seconds
  const getTimeRangeSeconds = (range: TimeRange): number => {
    const unit = range.slice(-1);
    const value = parseInt(range.slice(0, -1));

    switch (unit) {
      case 'h':
        return value * 3600;
      case 'd':
        return value * 86400;
      default:
        return 86400;
    }
  };

  // Fetch performance metrics
  const fetchMetrics = async (isBackground = false) => {
    if (!selectedEdgeId) return;

    if (!isBackground) setLoading(true);

    try {
      const options = isBackground ? { showLoading: false } : undefined;
      const end = Math.floor(Date.now() / 1000);
      const rangeSeconds = getTimeRangeSeconds(timeRange);
      const start = end - rangeSeconds;
      const step = Math.floor(rangeSeconds / 100); // ~100 data points

      // Fetch metrics in parallel
      const [
        rxBytesRes,
        txBytesRes,
        cpuRes,
        memoryBytesRes,
        memoryTotalRes,
        connectionsRes,
        rxDroppedRes,
        txDroppedRes,
        rxErrorsRes,
        txErrorsRes,
      ] = await Promise.all([
        observabilityService.queryRange(`rate(edge_interface_rx_bytes_total{edge_id="${selectedEdgeId}"}[5m])`, start, end, step, options),
        observabilityService.queryRange(`rate(edge_interface_tx_bytes_total{edge_id="${selectedEdgeId}"}[5m])`, start, end, step, options),
        observabilityService.queryRange(`edge_cpu_usage_percent{edge_id="${selectedEdgeId}"}`, start, end, step, options),
        observabilityService.queryRange(`edge_memory_bytes{edge_id="${selectedEdgeId}"}`, start, end, step, options),
        observabilityService.queryRange(`edge_memory_total_bytes{edge_id="${selectedEdgeId}"}`, start, end, step, options),
        observabilityService.queryRange(`edge_security_active_flows{edge_id="${selectedEdgeId}"}`, start, end, step, options),
        observabilityService.queryRange(`rate(edge_interface_rx_dropped_total{edge_id="${selectedEdgeId}"}[5m])`, start, end, step, options),
        observabilityService.queryRange(`rate(edge_interface_tx_dropped_total{edge_id="${selectedEdgeId}"}[5m])`, start, end, step, options),
        observabilityService.queryRange(`rate(edge_interface_rx_errors_total{edge_id="${selectedEdgeId}"}[5m])`, start, end, step, options),
        observabilityService.queryRange(`rate(edge_interface_tx_errors_total{edge_id="${selectedEdgeId}"}[5m])`, start, end, step, options),
      ]);

      // Process bandwidth data — rate() returns bytes/sec, convert to Mbps
      // Aggregate across all interfaces (sum series at each timestamp)
      const bandwidth: any[] = [];
      if (rxBytesRes.status === 'success' && rxBytesRes.data?.result?.length > 0) {
        // Build timestamp-indexed maps for aggregation across interfaces
        const rxByTs = new Map<number, number>();
        const txByTs = new Map<number, number>();

        for (const series of rxBytesRes.data.result) {
          for (const [ts, val] of (series.values || [])) {
            rxByTs.set(ts, (rxByTs.get(ts) || 0) + parseFloat(val));
          }
        }
        for (const series of (txBytesRes.data?.result || [])) {
          for (const [ts, val] of (series.values || [])) {
            txByTs.set(ts, (txByTs.get(ts) || 0) + parseFloat(val));
          }
        }

        // Merge timestamps and convert bytes/sec to Mbps
        const allTimestamps = [...new Set([...rxByTs.keys(), ...txByTs.keys()])].sort();
        for (const ts of allTimestamps) {
          const rxBytesPerSec = rxByTs.get(ts) || 0;
          const txBytesPerSec = txByTs.get(ts) || 0;
          bandwidth.push({
            x: ts * 1000,
            rx: Math.max(0, (rxBytesPerSec * 8) / 1_000_000),
            tx: Math.max(0, (txBytesPerSec * 8) / 1_000_000),
          });
        }
      }
      setBandwidthData(bandwidth);

      // Process CPU data
      const cpu: any[] = [];
      if (cpuRes.status === 'success' && cpuRes.data?.result?.length > 0) {
        const values = cpuRes.data.result[0].values || [];
        values.forEach(([ts, val]: [number, string]) => {
          cpu.push({ x: ts * 1000, y: parseFloat(val) });
        });
      }
      setCpuData(cpu);

      // Process memory data (convert to percentage)
      const memory: any[] = [];
      const memoryValues = memoryBytesRes.data?.result?.[0]?.values || [];
      const totalMemory = memoryTotalRes.data?.result?.[0]?.values?.[0]?.[1];
      const totalMemoryBytes = totalMemory ? parseFloat(totalMemory) : 1;

      memoryValues.forEach(([ts, val]: [number, string]) => {
        const usedBytes = parseFloat(val);
        const percentage = (usedBytes / totalMemoryBytes) * 100;
        memory.push({ x: ts * 1000, y: percentage });
      });
      setMemoryData(memory);

      // Process connections data
      const connections: any[] = [];
      if (connectionsRes.status === 'success' && connectionsRes.data?.result?.length > 0) {
        const values = connectionsRes.data.result[0].values || [];
        values.forEach(([ts, val]: [number, string]) => {
          connections.push({ x: ts * 1000, y: parseFloat(val) });
        });
      }
      setConnectionsData(connections);

      // Process packet loss data — rate() returns per-second values directly
      // Aggregate across all interfaces at each timestamp
      const packetLoss: any[] = [];
      const lossByTs = new Map<number, number>();

      for (const res of [rxDroppedRes, txDroppedRes, rxErrorsRes, txErrorsRes]) {
        for (const series of (res.data?.result || [])) {
          for (const [ts, val] of (series.values || [])) {
            lossByTs.set(ts, (lossByTs.get(ts) || 0) + parseFloat(val));
          }
        }
      }

      const lossTimes = [...lossByTs.keys()].sort();
      for (const ts of lossTimes) {
        packetLoss.push({
          x: ts * 1000,
          y: Math.max(0, lossByTs.get(ts) || 0),
        });
      }
      setPacketLossData(packetLoss);

      // Calculate averages
      const cpuAvg = cpu.length > 0 ? cpu.reduce((sum, d) => sum + d.y, 0) / cpu.length : 0;
      const memAvg = memory.length > 0 ? memory.reduce((sum, d) => sum + d.y, 0) / memory.length : 0;
      const bwAvg = bandwidth.length > 0 ? bandwidth.reduce((sum, d) => sum + d.rx, 0) / bandwidth.length : 0;
      const connAvg = connections.length > 0 ? connections.reduce((sum, d) => sum + d.y, 0) / connections.length : 0;

      setAvgCpu(Math.round(cpuAvg));
      setAvgMemory(Math.round(memAvg));
      setAvgBandwidth(Math.round(bwAvg));
      setAvgConnections(Math.round(connAvg));

      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Failed to fetch performance metrics:', error);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedEdgeId) {
      fetchMetrics(false);
      const interval = setInterval(() => fetchMetrics(true), 60000); // Refresh every minute
      return () => clearInterval(interval);
    }
  }, [selectedEdgeId, timeRange]);

  // Common chart options
  const commonChartOptions: ApexOptions = {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true },
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2 },
    grid: {
      borderColor: '#f1f5f9',
      strokeDashArray: 4,
    },
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeUTC: false,
        style: { colors: '#64748b', fontSize: '11px' },
      },
    },
    fill: { type: 'none' },
    yaxis: {
      labels: {
        style: { colors: '#64748b', fontSize: '11px' },
        formatter: (val: number) => typeof val === 'number' ? val.toFixed(1) : val,
      },
    },
    tooltip: {
      x: { formatter: createChartTooltipFormatter(dateSettings) },
    },
  };

  // Bandwidth Chart
  const bandwidthOptions: ApexOptions = {
    ...commonChartOptions,
    colors: ['#3b82f6', '#10b981'],
    yaxis: {
      ...commonChartOptions.yaxis,
      title: { text: 'Mbps', style: { color: '#64748b' } },
    },
  };

  const bandwidthSeries = [
    {
      name: 'Download',
      data: bandwidthData.map(d => ({ x: d.x, y: d.rx })),
    },
    {
      name: 'Upload',
      data: bandwidthData.map(d => ({ x: d.x, y: d.tx })),
    },
  ];

  // CPU Chart
  const cpuOptions: ApexOptions = {
    ...commonChartOptions,
    colors: ['#f59e0b'],
    yaxis: {
      ...commonChartOptions.yaxis,
      max: 100,
      title: { text: '%', style: { color: '#64748b' } },
    },
  };

  const cpuSeries = [
    {
      name: 'CPU Usage',
      data: cpuData,
    },
  ];

  // Memory Chart
  const memoryOptions: ApexOptions = {
    ...commonChartOptions,
    colors: ['#8b5cf6'],
    yaxis: {
      ...commonChartOptions.yaxis,
      max: 100,
      title: { text: '%', style: { color: '#64748b' } },
    },
  };

  const memorySeries = [
    {
      name: 'Memory Usage',
      data: memoryData,
    },
  ];

  // Connections Chart
  const connectionsOptions: ApexOptions = {
    ...commonChartOptions,
    colors: ['#06b6d4'],
    yaxis: {
      ...commonChartOptions.yaxis,
      title: { text: 'Connections', style: { color: '#64748b' } },
    },
  };

  const connectionsSeries = [
    {
      name: 'Connections',
      data: connectionsData,
    },
  ];

  // Packet Loss Chart
  const packetLossOptions: ApexOptions = {
    ...commonChartOptions,
    colors: ['#ef4444'],
    yaxis: {
      ...commonChartOptions.yaxis,
      title: { text: 'Packets/sec', style: { color: '#64748b' } },
    },
  };

  const packetLossSeries = [
    {
      name: 'Packet Loss',
      data: packetLossData,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Performance Monitoring</h1>
          <p className="text-sm text-gray-500 mt-1">Edge device performance metrics and analysis</p>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-500 hidden sm:flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            {formatTimeOnly(lastRefreshed, dateSettings)}
          </span>
          <button
            onClick={() => fetchMetrics(false)}
            disabled={loading || !selectedEdgeId}
            className="btn-secondary inline-flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Edge and Time Range Selection */}
      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Edge Device</label>
          <EdgeSelector
            selectedEdgeId={selectedEdgeId}
            onEdgeChange={setSelectedEdgeId}
          />
        </div>
        <div className="w-48">
          <label className="block text-sm font-medium text-gray-700 mb-1">Time Range</label>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as TimeRange)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="1h">Last Hour</option>
            <option value="6h">Last 6 Hours</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>
      </div>

      {!selectedEdgeId ? (
        <div className="card p-12 text-center">
          <Activity className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No Edge Selected</h3>
          <p className="text-gray-600 mt-2">Select an edge device to view performance metrics.</p>
        </div>
      ) : (
        <>
          {/* Metric Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={Cpu}
              title="Avg CPU Usage"
              value={`${avgCpu}%`}
              subtitle={`Last ${timeRange}`}
              color="warning"
            />
            <StatCard
              icon={HardDrive}
              title="Avg Memory"
              value={`${avgMemory}%`}
              subtitle={`Last ${timeRange}`}
              color="secondary"
            />
            <StatCard
              icon={TrendingUp}
              title="Avg Bandwidth"
              value={avgBandwidth}
              subtitle="Mbps"
              color="primary"
            />
            <StatCard
              icon={Activity}
              title="Avg Connections"
              value={avgConnections.toLocaleString()}
              subtitle="Active"
              color="info"
            />
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bandwidth Chart */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Network Bandwidth</h3>
              {loading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : bandwidthData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  <p>No data available</p>
                </div>
              ) : (
                <ReactApexChart
                  options={bandwidthOptions}
                  series={bandwidthSeries}
                  type="line"
                  height={280}
                />
              )}
            </div>

            {/* CPU Chart */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">CPU Utilization</h3>
              {loading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : cpuData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  <p>No data available</p>
                </div>
              ) : (
                <ReactApexChart
                  options={cpuOptions}
                  series={cpuSeries}
                  type="line"
                  height={280}
                />
              )}
            </div>

            {/* Memory Chart */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Memory Utilization</h3>
              {loading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : memoryData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  <p>No data available</p>
                </div>
              ) : (
                <ReactApexChart
                  options={memoryOptions}
                  series={memorySeries}
                  type="line"
                  height={280}
                />
              )}
            </div>

            {/* Connections Chart */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Connection Count</h3>
              {loading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : connectionsData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  <p>No data available</p>
                </div>
              ) : (
                <ReactApexChart
                  options={connectionsOptions}
                  series={connectionsSeries}
                  type="line"
                  height={280}
                />
              )}
            </div>
          </div>

          {/* Packet Loss Chart - Full Width */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Packet Loss Rate</h3>
            {loading ? (
              <div className="h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : packetLossData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-gray-500">
                <p>No data available</p>
              </div>
            ) : (
              <ReactApexChart
                options={packetLossOptions}
                series={packetLossSeries}
                type="line"
                height={280}
              />
            )}
          </div>

          {/* Performance Alerts */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Performance Alerts</h3>
              {alertsLoading && (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
              )}
            </div>
            <div className="space-y-2">
              {performanceAlerts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm">No active performance alerts</p>
                </div>
              ) : (
                performanceAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`flex items-start space-x-3 p-3 rounded-lg border ${
                    alert.severity === 'Critical'
                      ? 'bg-danger-50 border-danger-200'
                      : alert.severity === 'Warning'
                      ? 'bg-warning-50 border-warning-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}
                >
                  <AlertTriangle
                    className={`w-5 h-5 mt-0.5 ${
                      alert.severity === 'Critical'
                        ? 'text-danger-600'
                        : alert.severity === 'Warning'
                        ? 'text-warning-600'
                        : 'text-blue-600'
                    }`}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">{alert.device}</p>
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded ${
                          alert.severity === 'Critical'
                            ? 'bg-danger-100 text-danger-700'
                            : alert.severity === 'Warning'
                            ? 'bg-warning-100 text-warning-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {alert.severity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{alert.message}</p>
                    <p className="text-xs text-gray-500 mt-1">{alert.timestamp}</p>
                  </div>
                </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Performance;
